import { z } from 'zod';

/** RFC 4122 UUID (any version nibble 1–5). */
export function isRfc4122Uuid(value: string): boolean {
  const v = value.trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
}

/** Prisma default `cuid()` shape (25 chars, leading `c`). */
export function isPrismaCuid(value: string): boolean {
  const v = value.trim();
  return /^c[a-z0-9]{24}$/i.test(v);
}

/** Tier-1 property FK / route intake: production Property rows are UUID-shaped seeds or Prisma cuids — rejects obvious legacy slugs. */
export const zPhase5bTier1PropertyScopedId = z.string().refine((s) => isRfc4122Uuid(s) || isPrismaCuid(s), {
  message: 'propertyId must be an RFC 4122 UUID or a Prisma cuid',
});

/** Tier-1 unit FK: UUID, cuid, or deterministic `unit_*` seed ids until Unit PK pivot; empty string coerced to undefined. */
export const zPhase5bTier1UnitScopedIdNullish = z.preprocess(
  (v) => (v === '' ? undefined : v),
  z
    .union([
      z.undefined(),
      z.null(),
      z.string().refine((s) => isRfc4122Uuid(s) || isPrismaCuid(s) || s.trim().startsWith('unit_'), {
        message: 'unitId must be UUID, cuid, or unit_* seed id',
      }),
    ])
    .optional(),
);

/** Recurring bill template id: UUID/cuid in prod; `rec-*` test keys; `recurring_bill_*` seed templates. */
export const zPhase5bTier1RecurringBillTemplateId = z.string().refine(
  (s) => {
    const t = s.trim();
    return (
      isRfc4122Uuid(t) ||
      isPrismaCuid(t) ||
      /^rec-[a-z0-9_-]{1,80}$/i.test(t) ||
      /^recurring_bill_[a-z0-9_-]{1,200}$/i.test(t)
    );
  },
  { message: 'id must be UUID, cuid, rec-*, or recurring_bill_* template key' },
);
